import { supabase } from '../api/supabase'

export interface LinkableJob {
  id: string
  job_ref: string
  job_type: string
  direction: string
  transport_mode: string
  status: string | null
  boxtrax_synced: boolean
  parent_job_id: string | null
  parentRef: string | null
  // Why this job can't be linked (null = linkable). Shown to staff verbatim.
  blockedReason: string | null
}

// Z93: server-side job lookup for the Consol "Link existing job" picker -- never loads the jobs
// table (449 today, past PostgREST's 1000-row cap soon; a silently truncated list would send staff
// off to create a duplicate job). Same shape as searchCustomers (#103/#94): ilike + limit 50.
// Blocked jobs are RETURNED with a reason instead of hidden, so staff see why, not "not found".
export async function searchLinkableJobs(opts: { term: string; masterId: string; limit?: number }): Promise<LinkableJob[]> {
  const { term, masterId, limit = 50 } = opts
  const t = term.trim().replace(/[%,()*]/g, ' ').trim()
  if (t.length < 2) return []
  const { data, error } = await supabase
    .from('jobs')
    .select('id, job_ref, job_type, direction, transport_mode, status, boxtrax_synced, parent_job_id')
    .ilike('job_ref', `%${t}%`)
    .order('job_ref', { ascending: false })
    .limit(limit)
  if (error) throw new Error(error.message)
  const rows = (data ?? []) as Array<Omit<LinkableJob, 'parentRef' | 'blockedReason'>>

  const parentIds = [...new Set(rows.map(r => r.parent_job_id).filter((x): x is string => !!x))]
  const parentRefs = new Map<string, string>()
  if (parentIds.length) {
    const { data: parents, error: pErr } = await supabase.from('jobs').select('id, job_ref').in('id', parentIds)
    if (pErr) throw new Error(pErr.message)
    for (const p of (parents ?? []) as Array<{ id: string; job_ref: string }>) parentRefs.set(p.id, p.job_ref)
  }

  return rows.map(r => {
    const parentRef = r.parent_job_id ? parentRefs.get(r.parent_job_id) ?? null : null
    let blockedReason: string | null = null
    if (r.id === masterId) blockedReason = 'This is the master itself'
    else if (r.job_type === 'CONSOL') blockedReason = 'This is a Consol master — a master cannot be a child of another master'
    else if (r.parent_job_id === masterId) blockedReason = 'Already a child of this master'
    else if (r.parent_job_id) blockedReason = `Already a child of ${parentRef ?? 'another master'} — unlink it there first`
    return { ...r, boxtrax_synced: !!r.boxtrax_synced, parentRef, blockedReason }
  })
}
